<template>
  <div v-scroll="onScroll">
    <v-img
      v-if="albumInfo.thumbnailLarge"
      :src="albumInfo.thumbnailLarge"
      :lazy-src="albumInfo.thumbnail"
      :alt="`Photo by ${albumOwner.fullName}`"
      :draggable="false"
      class="main-gallery-image mb-3"
      cover
      @click.right.stop.prevent="onRightClick"
    >
      <template #placeholder>
        <v-row class="fill-height ma-0" align="center" justify="center">
          <v-progress-circular
            indeterminate
            color="tertiary"
          ></v-progress-circular>
        </v-row>
      </template>
      <v-btn
        v-if="hasFrom"
        class="my-3 ml-3 close-btn"
        color="secondary"
        absolute
        outlined
        small
        depressed
        @click="goBack"
      >
        <v-icon small class="mr-3">
          fal fa-long-arrow-left
        </v-icon>
        <span>Back</span>
      </v-btn>
    </v-img>
    <div v-else class="main-gallery-image d-flex align-center justify-center">
      <p class="ma-0 text-body-1">No thumbnail set</p>
    </div>
    <Actions
      :me="me"
      :album="albumInfo"
      :info="albumInfoMe"
      :owner="albumOwner"
    />
    <v-container v-if="mounted">
      <v-row justify="center">
        <v-col cols="12" md="10" lg="8" xl="6" class="pt-0">
          <div class="text-center">
            <v-alert
              v-if="!albumInfo.isPrivate && albumInfo.totalNumberOfPhotos < 3"
              dismissible
              type="info"
              class="mb-6"
              close-icon="fa fa-times fa-sm"
              icon="fal fa-info-circle fa-sm"
            >
              This Album is visible only to you. Please add a minimum of 3
              photos to make it publicly visible
            </v-alert>
            <h1 class="text-h4 font-weight-bold mb-3">
              {{ albumInfo.title }}
            </h1>
            <h2 class="text-h6">
              <small>by</small>
              <nuxt-link
                :to="`/photographer/${albumOwner.userName || albumOwner.id}`"
                class="no-underline font-weight-bold"
              >
                {{ albumOwner.fullName }}
              </nuxt-link>
            </h2>
            <h3 class="text-body-1">
              {{ albumInfo.totalNumberOfPhotos }} photo{{
                albumInfo.totalNumberOfPhotos === 1 ? '' : 's'
              }}
            </h3>
            <h4 class="text-caption mb-6">
              Album updated {{ albumInfo.lastUpdated | timeago }}
            </h4>
            <p class="text-body-1 mb-4">
              {{ albumInfo.description }}
            </p>
            <div
              v-if="albumInfo && albumInfo.tags && albumInfo.tags.length > 0"
              class="my-6"
            >
              <h5 class="text-body-1 d-block font-weight-bold">Tagged as:</h5>
              <span
                v-for="(tag, i) in albumInfo.tags"
                :key="i"
                class="text-body-2 font-italic d-inline-block mr-2"
              >
                #{{ tag }}
              </span>
            </div>
          </div>
        </v-col>
      </v-row>
    </v-container>
    <v-container fluid>
      <v-row>
        <v-col cols="12" class="px-0">
          <Gallery
            v-if="mounted"
            :photos="albumPhotos"
            :me="me"
            :load-more="loadMore"
            :auto-load-disabled="autoLoadDisabled"
            :loading="loading"
            :get-queries="getQueries"
            :gallery-owner-id="albumOwner.id"
            from="albums"
          />
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import moment from 'moment'
import { mapState } from 'vuex'
import Gallery from '@/components/galleries/Gallery.vue'
import Actions from '@/components/galleries/album/Actions.vue'
import { EventBus } from '@/plugins/event-bus'

export default {
  filters: {
    timeago: date => {
      return moment
        .utc(date)
        .local()
        .fromNow()
    }
  },
  components: {
    Gallery,
    Actions
  },
  middleware: ['header'],
  async asyncData({ params, $axios, store, $asa }) {
    try {
      let seoData = {}
      seoData = await $axios.$get(`/albums/${params.id}/seo`)
      return {
        seoData
      }
    } catch (error) {
      $asa.appInsights('Pages: albums/_id/_slug.vue - method: asyncData', error)
    }
  },
  data() {
    return {
      mounted: false,
      loading: false,
      offsetTop: 0
    }
  },
  async fetch({ store, params, $asa }) {
    try {
      const data = {
        albumId: params.id,
        page: 1
      }
      await store.dispatch('albums/resetAlbum')
      await store.dispatch('albums/getAlbum', data)
    } catch (error) {
      $asa.appInsights('Pages: albums/_id/_slug.vue - method: fetch', error)
    }
  },
  head({ $seo }) {
    return $seo({
      title: `${this.seoData.title} by ${this.seoData.ownerFullName}`,
      description: `Learn from top photographers. Check the photo's album of ${this.seoData.ownerFullName}.`,
      url: `https://100asa.com/albums/${this.seoData.id}/${this.seoData.slug}`,
      image: this.seoData.thumbnail
    })
  },
  computed: {
    ...mapState({
      me: state => state.auth.user,
      albumPhotos: state => state.albums.albumPhotos,
      albumPhotosIsLastPage: state => state.albums.albumPhotosIsLastPage,
      albumPhotosPage: state => state.albums.albumPhotosPage,
      albumInfo: state => state.albums.albumInfo,
      albumOwner: state => state.albums.albumOwner,
      albumInfoMe: state => state.albums.albumInfoMe
    }),
    autoLoadDisabled() {
      return this.loading || !this.mounted || this.albumPhotos.length === 0
    },
    hasFrom() {
      return !!this.$route.query.from
    }
  },
  async mounted() {
    const albumId = this.$route.params.id
    if (albumId) {
      try {
        this.loading = true
        if (this.me && this.me.id) {
          await this.$store.dispatch('albums/getAlbumInfoMe', albumId)
        }
      } catch (error) {
        this.$asa.appInsights('Pages: albums/_id/_slug.vue - method: mounted', error)
      } finally {
        this.loading = false
        this.mounted = true
      }
    } else {
      this.goBack()
    }
  },
  methods: {
    getQueries() {
      return `&st=${this.offsetTop}&aid=${this.albumInfo.id}`
    },
    goBack() {
      const q = this.$route.query
      let path

      switch (q.from) {
        case 'albums':
          path = `/albums?st=${q.st}`
          break
        case 'photo':
          path = `/photo/${q.id}`
          break
        case 'photographer':
          path = `/photographer/${q.pid}?t=1`
          break
        case 'account':
          path = `/account/albums`
          break
        default:
          path = '/'
          break
      }
      this.$router.push(path)
    },
    async loadMore() {
      if (!this.albumPhotosIsLastPage && !this.loading && this.mounted) {
        try {
          this.loading = true
          const nextPage = this.albumPhotosPage + 1
          const data = {
            albumId: this.$route.params.id,
            page: nextPage
          }
          await this.$store.dispatch('albums/getAlbum', data)
        } catch (error) {
          this.$asa.appInsights('Pages: albums/_id/_slug.vue - method: loadMore', error)
        } finally {
          this.loading = false
        }
      }
    },
    onScroll() {
      setTimeout(() => {
        const top = document.getElementById('app').getBoundingClientRect().top * -1
        this.offsetTop = top
      })
    },
    onRightClick() {
      const text = `This photo is © of ${this.albumOwner.fullName}`
      EventBus.$emit('snackbar', {
        text,
        color: 'info'
      })
    }
  }
}
</script>

<style scoped>
.main-gallery-image {
  height: 80vh;
}
</style>
